import React, { useCallback, useEffect } from 'react'

type Refs = Array<React.RefObject<HTMLElement> | string>

// Resolve a list of refs or DOM IDs to a list of `{current: HTMLElement}`
// Refs is `refs` are unchanged, while DOM IDs are resolved.
function resolveRefs(refs: Refs) {
  return refs.map(r => {
    if (typeof r === 'string') {
      return { current: document.getElementById(r) }
    }

    return r
  })
}

// `ref` can be an HTML ref object, or a list of mixed ref objects and DOM IDs.
// `exceptClass` is a class or list of classes to consider "inside" by this hook.
export default function useOnClickOutside(
  ref: React.RefObject<HTMLElement> | Refs,
  callback: () => void,
  isActive = true,
  exceptClass?: string | string[],
) {
  const checkExceptions = useCallback(
    ({ target }: MouseEvent) => {
      const targetClassName = (target as HTMLElement | null)?.className

      if (!exceptClass || typeof targetClassName !== 'string') {
        return true
      }

      if (Array.isArray(exceptClass)) {
        return exceptClass.every(exc => targetClassName.indexOf(exc) < 0)
      }

      return targetClassName.indexOf(exceptClass) < 0
    },
    [exceptClass],
  )

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      let forwardClickOutside

      if (Array.isArray(ref)) {
        const refs = resolveRefs(ref)

        forwardClickOutside = refs.every(refObj => refObj.current && !refObj.current.contains(event.target as HTMLElement)) && checkExceptions(event)
      } else {
        forwardClickOutside = ref.current && !ref.current.contains(event.target as HTMLElement) && checkExceptions(event)
      }

      if (forwardClickOutside) {
        callback()
      }
    },
    [callback, checkExceptions, ref],
  )

  useEffect(() => {
    if (isActive) {
      window.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isActive, handleClickOutside])
}
